import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar,
    Box,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from "@mzc-pdc/ui";
import convertToByte from "@cores/convertToByte";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconAi, IconCopy, IconDrm, IconLink, IconLock, IconOpenInNew, IconStar } from "@mzc-cloudplex/icons";
import TableCard from "@components_v2/card/table-card";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { useInView } from "react-intersection-observer";
import StatusBadge from "@components_v2/status-badge/status-badge";
import { useTranslation } from "react-i18next";

import { getProjectRandomColor } from "@utils/get-random-color";
import { Link } from "react-router-dom";
import formatDuration from "@cores/formatDuration";
import ContentTypeFlag from "@components_v2/icon-type/content/content-type-flag";
import { CircledIcon } from "@components_v2/icon";
import { alpha } from "@mui/material";
import { useAutocompleteTable } from "@hooks/useAutocompleteTable";
import Skeleton from "@components_v2/skeleton/skeleton";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { RandomColoredAvatar } from "@components_v2/avatar/random-colored-avatar";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import useSettings from "@hooks/useSettings";
import { ASSET_PREVIEW_STATUS, MENU_LIST } from "@constants";
import { canNavigateToArchiveDetail } from "@utils/archive-utils";
import { capitalize } from "@utils/convert-string";
import ContentMediaImage from "@features/content-media-image/content-media-image";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import { EllipsisTypography, OverflowTooltip } from "@components_v2/ellipsis-tooltip";

const AutocompleteTableList = ({
    data,
    type,
    subType,
    columns,
    getMenuActions,
    viewMore,
    onClickName,
    openNewWindow,
    noDataContent,
    divider,
    gridStyle,
    listStyle,
    loadingTooltip,
    thumbnailCrop = false,
    showAdditionalIcons = false, //Note; media type 아이콘을 제외한 나머지 아이콘(아카이브, AI、 좋아요 등)을 노출할 것인지 선택
    onFetchData = () => {},
    onClickRow = () => {},
}) => {
    const { t } = useTranslation();
    const [targetRef, inView] = useInView();
    const tableRef = useRef();
    const { getTableColumns } = useAutocompleteTable();
    const { navigatorMenus } = useSettings({ type: "General" });

    const [tableWidth, setTableWidth] = useState(null);
    const [selected, setSelected] = useState([]);

    const onClickCopyUrl = (event, url) => {
        let elem = document.createElement("textarea");
        document.body.appendChild(elem);
        elem.value = url;
        elem.select();
        document.execCommand("copy");
        document.body.removeChild(elem);
    };

    const getNameThumbnail = useCallback(
        (type, option, column) => {
            let accessUrl = "";
            switch (type.toUpperCase()) {
                case "ASSET": {
                    const commonStatusPreviews = option?.previews?.filter(
                        (p) => p.status !== ASSET_PREVIEW_STATUS.ERROR,
                    );
                    const previewStatus =
                        commonStatusPreviews?.length === 0 && option.previews?.length > 0
                            ? ASSET_PREVIEW_STATUS.ERROR
                            : commonStatusPreviews?.[0]?.status ?? ASSET_PREVIEW_STATUS.COMPLETE;

                    if (commonStatusPreviews?.[0]?.accessUrl) accessUrl = commonStatusPreviews?.[0]?.accessUrl;
                    else if (option?.thumbnails?.[0]?.accessUrl) accessUrl = option?.thumbnails?.[0]?.accessUrl;
                    else if (option?.accessUrls?.[0]) accessUrl = option?.accessUrls?.[0];
                    else if (option?.posters?.[0]?.accessUrl) accessUrl = option?.posters?.[0]?.accessUrl;
                    else if (option?.primaryPoster?.accessUrl) accessUrl = option?.primaryPoster?.accessUrl;
                    else if (option?.elements?.[0]?.accessUrl) accessUrl = option?.elements?.[0]?.accessUrl;
                    else accessUrl = option?.accessUrl;

                    return (
                        <AssetThumbnail
                            variant={"table"}
                            src={accessUrl}
                            sx={column?.style?.thumbnail}
                            thumbnailCrop={thumbnailCrop}
                            showIcons={showAdditionalIcons}
                            asset={option}
                            errorIconProps={{ position: "left", size: 16 }}
                            previewStatus={previewStatus}
                        />
                    );
                }
                case "CONTENT": {
                    return (
                        <MediaImageWrapper
                            sx={{
                                position: `relative`,
                                width: 40,
                                height: 40,
                                borderRadius: `4px`,
                                backgroundColor: alpha("#e5e7ed", 0.5),
                                overflow: `hidden`,
                            }}
                        >
                            <ContentMediaImage
                                contentType={option.contentType ?? subType}
                                variant={"table"}
                                errorIconProps={{ position: "left", size: 16 }}
                                data={option}
                                draggable={false}
                                isShowImagePreviewModal={false}
                            />
                        </MediaImageWrapper>
                    );
                }
                case "COLLECTION":
                    return <CollectionThumbnail collection={option} size={"xsmall"} variant={"table"} />;
            }
        },
        [data, showAdditionalIcons],
    );

    const getNameIcon = useCallback(
        (type, option) => {
            if (!option) return null;
            switch (type.toUpperCase()) {
                case "ASSET":
                    return <AssetTypeFlag size={"small"} type={option.mediaType} />;
                case "CONTENT":
                    return <ContentTypeFlag size={"small"} type={option.contentType ?? subType} />;
            }
        },
        [data],
    );

    const getSkeletonByColumn = (column, item) => {
        switch (column.id) {
            case "desiredStatus":
            case "status":
                return <Skeleton variant="rounded" animation={"wave"} width={12} height={12} />;
            case "nameWithSubName":
                return (
                    <Grid
                        container
                        direction={`row`}
                        justifyContent={`flex-start`}
                        alignItems={`center`}
                        flexWrap={"nowrap"}
                    >
                        <Grid item>
                            <Skeleton
                                variant="rounded"
                                animation={"wave"}
                                width={40}
                                height={40}
                                style={{ marginRight: "4px" }}
                            >
                                {item.loading && (
                                    <Tooltip
                                        title={t(
                                            `common::label::${loadingTooltip ?? "Loading"}`,
                                            `${loadingTooltip ?? "Loading"}`,
                                        )}
                                    >
                                        <CircularProgress color="blue" />
                                    </Tooltip>
                                )}
                            </Skeleton>
                        </Grid>
                        <Grid container flexDirection={"column"} gap={0.5}>
                            <Grid item sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                <Grid item ml={0.5} mr={1}>
                                    {["ASSET", "CONTENT"].includes(type) && getNameIcon(type, item) && (
                                        <Skeleton variant="rounded" animation={"wave"} width={22} height={16} />
                                    )}
                                </Grid>
                                <Grid flex={1} maxWidth={300}>
                                    <Skeleton variant="rounded" animation={"wave"} height={16} />
                                </Grid>
                            </Grid>
                            <Grid item sx={{ ml: 0.5, display: "flex", flexDirection: "row", alignItems: "center" }}>
                                <Skeleton variant="rounded" animation={"wave"} width={100} height={16} />
                            </Grid>
                        </Grid>
                    </Grid>
                );
            case "name":
                if (subType === "LINK")
                    return <Skeleton variant="rounded" animation={"wave"} width={300} height={18} />;
                return (
                    <Grid
                        container
                        direction={`row`}
                        justifyContent={`flex-start`}
                        alignItems={`center`}
                        flexWrap={"nowrap"}
                    >
                        <Grid item>
                            <Skeleton
                                variant="rounded"
                                animation={"wave"}
                                width={40}
                                height={40}
                                style={{ marginRight: "4px" }}
                            >
                                {item.loading && (
                                    <Tooltip
                                        title={t(
                                            `common::label::${loadingTooltip ?? "Loading"}`,
                                            `${loadingTooltip ?? "Loading"}`,
                                        )}
                                    >
                                        <CircularProgress color="blue" />
                                    </Tooltip>
                                )}
                            </Skeleton>
                        </Grid>
                        <Grid container flexDirection={"row"} alignItems={"center"}>
                            <Grid item ml={0.5} mr={1}>
                                {["ASSET", "CONTENT"].includes(type) && getNameIcon(type, item) && (
                                    <Skeleton variant="rounded" animation={"wave"} width={22} height={16} />
                                )}
                            </Grid>
                            <Grid flex={1} maxWidth={300}>
                                <Skeleton variant="rounded" animation={"wave"} height={21} />
                            </Grid>
                        </Grid>
                    </Grid>
                );
            default:
                return (
                    <Skeleton
                        variant="rounded"
                        animation={"wave"}
                        width={column.style?.custom?.width ?? 80}
                        height={22}
                    />
                );
        }
    };

    const getLinkComponent = useCallback(
        (type, item) => {
            let menuName = "";
            if (type.toUpperCase() === "ASSET" && canNavigateToArchiveDetail(item.archiveStatus)) {
                menuName = MENU_LIST.ARCHIVED;
            } else {
                let parsedType = type;
                if (type === "CONTENT") {
                    parsedType = subType;
                    if (!subType) {
                        //Note: Asset 상세 > Reference 탭은 여러 타입의 컨텐츠가 섞여있어서 subType props로 고정을 할 수 없음
                        parsedType = item.contentType;
                    }
                }
                menuName = `${parsedType?.toUpperCase()}S`;
            }

            if (!navigatorMenus?.[menuName]?.visible) {
                return (
                    <Tooltip
                        title={t(
                            `common::msg::It's not possible to proceed as the {{menuName}} menu isn't being used.`,
                            `It's not possible to proceed as the {{menuName}} menu isn't being used.`,
                            { menuName: t(`common::label::${capitalize(menuName, true)}`) },
                        )}
                    >
                        <Box sx={{ width: "fit-content" }}>
                            <Typography variant={"body1_500"} component={"span"} noWrap>
                                {item.name}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            }

            return (
                <OverflowTooltip title={item.name}>
                    <Link
                        style={{
                            color: theme.palette.greySecondary.main,
                            fontWeight: 500,
                            // 잘림 여부를 이 요소에서 측정하므로 말줄임 스타일을 직접 지정한다. (Link 는 noWrap prop 미지원)
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        target={openNewWindow ? "_blank" : null}
                        to={onClickName(item)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {item.name}
                    </Link>
                </OverflowTooltip>
            );
        },
        [navigatorMenus, onClickName, openNewWindow],
    );

    const getTableValueByColumn = useCallback(
        (id, option, column) => {
            switch (id) {
                case "desiredStatus":
                case "status":
                    return (
                        <Grid container justifyContent={"center"}>
                            <StatusBadge status={option?.status} size={16} />
                        </Grid>
                    );
                case "name":
                    if (subType === "LINK")
                        return (
                            <Grid
                                container
                                flexDirection={"row"}
                                alignItems={"center"}
                                gap={theme.spacing(1)}
                                flexWrap={"nowrap"}
                            >
                                <Grid item minWidth={20}>
                                    <Box
                                        height={16}
                                        sx={{
                                            backgroundColor: theme.palette.secondary[200],
                                            borderRadius: "2px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <IconLink size={12} htmlColor={theme.palette.common.white} />
                                    </Box>
                                </Grid>
                                <Grid item sx={{ width: "calc(100% - 20px)", display: "flex", alignItems: "center" }}>
                                    <Grid
                                        item
                                        sx={{
                                            display: "flex",
                                            overflow: "hidden",
                                            alignItems: "center",
                                        }}
                                    >
                                        <OverflowTooltip title={option.name}>
                                            <div
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    width: "100%",
                                                    minWidth: 0,
                                                    fontWeight: 500,
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                <a
                                                    href={option.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: `inherit`, textDecoration: "underline" }}
                                                >
                                                    {option.name}
                                                </a>
                                            </div>
                                        </OverflowTooltip>

                                        <Tooltip title={t(`common::label::${"Copy"}`)}>
                                            <IconButton
                                                circled
                                                onClick={(e) => {
                                                    onClickCopyUrl(e, option.url);
                                                }}
                                            >
                                                <IconCopy size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    return (
                        <TableCard
                            thumbnail={getNameThumbnail(type, option, column)}
                            name={{
                                name: onClickName ? (
                                    getLinkComponent(type, option)
                                ) : (
                                    <EllipsisTypography
                                        variant={"body1_500"}
                                        component={"span"}
                                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    >
                                        {option?.name}
                                    </EllipsisTypography>
                                ),
                                startIcon: getNameIcon(type, option),
                                endIcon: showAdditionalIcons && type === "ASSET" && (
                                    <Grid item>
                                        <Grid
                                            container
                                            flexDirection={"row"}
                                            gap={theme.spacing(0.75)}
                                            alignItems={"center"}
                                        >
                                            {option?.isConnected ? <IconLink size={16} /> : ""}
                                            {option?.myFavorite ? (
                                                <IconStar size={16} htmlColor={theme.palette.etc.orange500} />
                                            ) : (
                                                ""
                                            )}
                                            {option?.encryptions?.indexOf("DRM") > -1 && (
                                                <CircledIcon size={20} color={theme.palette.warning.main}>
                                                    <IconDrm size={12} htmlColor={theme.palette.constant.white} />
                                                </CircledIcon>
                                            )}
                                            {option?.isAnalyzed && (
                                                <CircledIcon size={20} color={theme.palette.success.light}>
                                                    <IconAi size={12} htmlColor={theme.palette.constant.white} />
                                                </CircledIcon>
                                            )}
                                            {option?.isOwnerLocked && (
                                                <CircledIcon size={20} color={theme.palette.etc.gray300}>
                                                    <IconLock size={12} htmlColor={theme.palette.constant.white} />
                                                </CircledIcon>
                                            )}
                                        </Grid>
                                    </Grid>
                                ),
                            }}
                            sx={{ ...column.style }}
                        />
                    );
                case "nameWithSubName":
                    return (
                        <TableCard
                            thumbnail={getNameThumbnail(type, option, column)}
                            name={{
                                name: (
                                    <EllipsisTypography
                                        variant={"body1_500"}
                                        component={"span"}
                                        sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {option?.name}
                                    </EllipsisTypography>
                                ),
                                startIcon: getNameIcon(type, option),
                            }}
                            subName={{
                                name: (
                                    <Grid
                                        container
                                        flexDirection={"row"}
                                        sx={{
                                            "& span:not(:last-child)": {
                                                pr: 5,
                                                borderRight: `solid 1px ${theme.palette.secondary[100]}`,
                                            },
                                            "& span:not(:first-of-type)": {
                                                pl: 1.5,
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant={"body3_400"}
                                            component={"span"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {`.${option?.extensions}`}
                                        </Typography>
                                        <Typography
                                            variant={"body3_400"}
                                            component={"span"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {option?.mediaType ?? "-"}
                                        </Typography>
                                        <Typography
                                            variant={"body3_400"}
                                            component={"span"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {option?.size ? convertToByte(option?.size) : "-"}
                                        </Typography>
                                    </Grid>
                                ),
                            }}
                        />
                    );
                case "projectName":
                    return (
                        <Grid container flexDirection={"row"} alignItems={"center"}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: 16,
                                    fontWeight: `bold`,
                                    bgcolor: getProjectRandomColor(option?.id),
                                    mr: 1,
                                }}
                            >
                                {option.name.charAt(0)}
                            </Avatar>

                            {/* 1.0.81 Note 프로젝트 누르면 어디로 이동? */}
                            <OverflowTooltip title={option.name}>
                                <Link
                                    target="_blank"
                                    //component={RouterLink}
                                    style={{
                                        color: theme.palette.secondary[800],
                                        fontWeight: 500,
                                        display: `inline-flex`,
                                        WebkitLineClamp: `2`,
                                        textOverflow: `ellipsis`,
                                        WebkitBoxOrient: `vertical`,
                                        maxHeight: 45,
                                        overflow: `hidden`,
                                        textDecoration: "none",
                                        "&:hover": {
                                            "#external-link-icon": {
                                                display: "block",
                                            },
                                        },
                                    }}
                                    noWrap
                                    to={onClickName && onClickName(option)}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {option.name}
                                    <Grid item id={"external-link-icon"} sx={{ display: "none" }}>
                                        <IconOpenInNew size={16} />
                                    </Grid>
                                </Link>
                            </OverflowTooltip>
                        </Grid>
                    );
                case "size":
                    return <Grid item>{option?.size >= 0 ? convertToByte(option?.size) ?? "-" : null}</Grid>;

                case "count":
                    return (
                        <Grid item sx={{ textAlign: `right` }}>
                            {option?.assetTotalCount >= 0 ? (
                                <BadgeCollection totalCount={option?.assetTotalCount} />
                            ) : null}
                        </Grid>
                    );
                case "duration":
                    return <Grid item>{option?.duration ? formatDuration(option?.duration) : null}</Grid>;
                case "extensions":
                    return <Grid item>{option?.[id] ? `.${option?.[id]}` : null}</Grid>;
                case "user":
                    return (
                        <Stack direction={"row"} sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                            <Box>
                                <RandomColoredAvatar id={option.id} name={option.name} size={24} />
                            </Box>
                            <Box>
                                <Typography variant={"body1-500"} color={theme.palette.greySecondary.main}>
                                    {option.name}
                                </Typography>
                                &nbsp;
                                <Typography variant={"body1-400"} color={theme.palette.greySecondary[600]}>
                                    {option.username}
                                </Typography>
                            </Box>
                        </Stack>
                    );
                default:
                    return option?.[id];
            }
        },
        [data, type],
    );

    const getDefaultColumns = useCallback(
        (item) => {
            if (columns) return columns;

            //NOTE: columns가 없는 경우 autocomplete table columns에서 가져옴
            if (type === "ASSET") {
                return getTableColumns(type, item.mediaType);
            } else if (type === "CONTENT") {
                return getTableColumns(type, item.contentType ?? subType);
            } else if (type === "COLLECTION") {
                return getTableColumns(type);
            } else if (type === "LINK") {
                return getTableColumns(type);
            } else return [];
        },
        [type, subType],
    );

    const checkNoData = useMemo(() => {
        return (
            !data || data?.length === 0 || data?.reduce((acc, cur) => acc.concat(cur?.value ?? []), [])?.length === 0
        );
    }, [data]);

    const getCustomCellWidth = useCallback(
        (column) => {
            if (!column.style?.custom) return "auto";
            const MENU_ACTION_WIDTH = getMenuActions ? 50 : 0;
            if (!tableRef.current) return column.style.custom.width;

            if (column.id === "name") {
                const COLUMN_NAME_WIDTH = 200;
                const _tableWidth = tableWidth ?? tableRef.current.getBoundingClientRect().width;
                const cellWidth =
                    MENU_ACTION_WIDTH +
                    columns
                        ?.filter((el) => el.id !== "name")
                        .reduce((acc, cur) => acc + cur.style.custom.width ?? 0, 0);
                if (_tableWidth - COLUMN_NAME_WIDTH <= cellWidth) return COLUMN_NAME_WIDTH;
                return `calc(100% - ${cellWidth}px - ${MENU_ACTION_WIDTH}px)`;
            }
            return column.style.custom.width;
        },
        [tableWidth],
    );

    useEffect(() => {
        if (!tableRef.current) return;
        let rafId;
        const resizeObserver = new ResizeObserver((entries) => {
            rafId = requestAnimationFrame(() => {
                for (const entry of entries) {
                    setTableWidth(entry.contentRect.width);
                }
            });
        });
        resizeObserver.observe(tableRef.current);

        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, [type]);

    useEffect(() => {
        if (inView) {
            onFetchData();
        }
    }, [inView]);

    if (checkNoData && noDataContent) return noDataContent;
    else if (data?.length > 0)
        return (
            <Grid
                item
                sx={{
                    borderRadius: `4px`,
                    border: `1px solid ${theme.palette.greySecondary[100]}`,
                    overflowY: "auto",
                    ...gridStyle,
                }}
            >
                {data?.map((v) => {
                    if (v.value?.length === 0) return null;
                    return (
                        <>
                            {v.subtitle && (
                                <>
                                    <Grid item ml={2.5} mt={2.5} mb={1}>
                                        <Typography variant={"body1_500"} color={theme.palette.secondary[800]}>
                                            {v.subtitle}
                                        </Typography>
                                    </Grid>
                                    <Divider />
                                </>
                            )}
                            <Table
                                sx={{
                                    width: "100%",
                                    padding: "0",
                                    overflowY: "auto",
                                    tableLayout: "fixed",
                                    ...listStyle,
                                }}
                            >
                                <TableBody ref={tableRef}>
                                    {v.value?.map((item, rowIndex) => (
                                        <React.Fragment key={`autocomplete-table-list-${item.id}`}>
                                            <TableRow
                                                sx={{
                                                    padding: `${
                                                        getMenuActions ? `4px 56px 4px 8px` : `4px 12px 4px 8px`
                                                    }`,
                                                    ...(onClickRow && {
                                                        "&:hover": {
                                                            backgroundColor: alpha(
                                                                theme.palette.greySecondary[50],
                                                                0.24,
                                                            ),
                                                        },
                                                        ...(selected?.id === item.id && {
                                                            backgroundColor: `${alpha(
                                                                theme.palette.greySecondary.A100,
                                                                0.36,
                                                            )} !important`,
                                                        }),
                                                    }),
                                                }}
                                                data-id={item.id}
                                                onClick={() => {
                                                    if (onClickRow && type !== "LINK") {
                                                        setSelected(item);
                                                        onClickRow(item);
                                                    }
                                                }}
                                            >
                                                {getDefaultColumns(item)?.map((column, index) => {
                                                    const nameIndex = getDefaultColumns(item).findIndex(
                                                        (c) => c.id === "name",
                                                    );
                                                    const prevColumn = getDefaultColumns(item)[index - 1];
                                                    return (
                                                        <TableCell
                                                            key={`autocomplete-table-list-${item.id}-${index}`}
                                                            mt={1}
                                                            mb={1}
                                                            sx={{
                                                                ...column.style.custom,
                                                                width: getCustomCellWidth(column),
                                                                ...(!gridStyle?.height &&
                                                                rowIndex === v.value.length - 1
                                                                    ? { borderBottom: "none" }
                                                                    : {}),
                                                            }}
                                                        >
                                                            <Grid container flexDirection={"row"} alignItems={"center"}>
                                                                {index > nameIndex + 1 &&
                                                                    divider &&
                                                                    (item[prevColumn.id] ? (
                                                                        <Divider
                                                                            orientation="vertical"
                                                                            flexItem
                                                                            style={{ marginRight: "16px" }}
                                                                        />
                                                                    ) : (
                                                                        <div style={{ marginRight: "16px" }}></div>
                                                                    ))}
                                                                {item.pending
                                                                    ? getSkeletonByColumn(column, item)
                                                                    : getTableValueByColumn(column.id, item, column)}
                                                            </Grid>
                                                        </TableCell>
                                                    );
                                                })}
                                                {getMenuActions && getMenuActions(item) && (
                                                    <TableCell
                                                        align={"center"}
                                                        sx={{
                                                            width: 50,
                                                            ...(!gridStyle?.height && rowIndex === v.value.length - 1
                                                                ? { borderBottom: "none" }
                                                                : {}),
                                                        }}
                                                    >
                                                        {!item.pending ? (
                                                            getMenuActions(item)
                                                        ) : (
                                                            <Skeleton
                                                                variant="circular"
                                                                animation={"wave"}
                                                                width={32}
                                                                height={32}
                                                            />
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                    {viewMore && <div ref={targetRef} id={"loadMoreDiv"} />}
                                </TableBody>
                            </Table>
                        </>
                    );
                })}
            </Grid>
        );
    return null;
};

export default AutocompleteTableList;
